import * as React from 'react';
import { useEffect, useState } from 'react';
import { toast } from 'chameleon/ui-stack/ui/toast';
import useNavigation from 'chameleon/ui-stack/hooks/useNavigation';

import Context from '../state/context';
import { calculateCache } from './utils';

import {
  createLabelAPI,
  createLabelItemAssociation,
  createLabelResourceAssociation,
  createLabelItemAssociationsBulk,
  createLabelResourceAssociationsBulk,
  updateLabelAPI,
  deleteLabelAPI,
  updateLabelsVisibilityAPI,
  updateLabelAssociationsBulkAPI,
  reorderLabelsAPI,
} from './utils/api.utils';
import { convertObjectKeysToCamelCase } from './utils/apiHelpers';

import {
  addLabels,
  removeLabels,
  reorderLabels as reorderLabelsUtil,
  updateLabels,
} from './utils/labels.utils';
import {
  handleTelemetry,
  TELEMETRY_EVENTS,
} from 'legacy-src/utility/telemetry';
import {
  resourcesForBulkInitState,
  resourceTypes,
} from 'legacy-src/components/TeacherOrganize/utils/constants';

function TeacherOrganizeProvider({
  children,
  subjects,
  router,
  isTeacherInfoLoading,
}) {
  /**
   * A map of label ID to the details of the full object
   * to allow easy lookup
   */

  const [labels, setLabels] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isReordering, setIsReordering] = useState(false);
  const [isBulkMode, setIsBulkMode] = useState(false);

  const [resourcesForBulk, setResourcesForBulk] = useState(
    resourcesForBulkInitState,
  );

  const {
    isAssignmentProgressPage,
    isAssignmentsPage,
    isSppTeacherPreviewPage,
  } = (router && useNavigation({ router })) || {};

  const updateLabelState = (labelData) =>
    setLabels(updateLabels(labels, labelData));
  const removeLabelState = (labelData) =>
    setLabels(removeLabels(labels, labelData));
  const addLabelState = (labelData) => setLabels(addLabels(labels, labelData));
  const updateLabelVisibilityState = (studentVisible, masterSubjectId) => {
    const labelsTemp = labels;
    const subjectLabels = labelsTemp[masterSubjectId]?.labels;
    subjectLabels &&
      Object.values(subjectLabels).forEach((label) => {
        labelsTemp[masterSubjectId].labels[label.labelId] = {
          ...label,
          studentVisible,
        };
      });
    setLabels(labelsTemp);
  };

  useEffect(() => {
    const labelsTemp = {};
    (subjects || []).forEach(({ id, labels: subjectLabels }) => {
      labelsTemp[id] = calculateCache({ subjectLabels });
    });
    setLabels(labelsTemp);
  }, [subjects]);

  useEffect(() => {
    const shouldDeactivateBulk = !(
      isAssignmentsPage ||
      isAssignmentProgressPage ||
      isSppTeacherPreviewPage
    );
    isBulkMode && shouldDeactivateBulk && deactivateBulkMode();
  }, [isAssignmentsPage, isAssignmentProgressPage, isSppTeacherPreviewPage]);

  const createLabel = async (label) => {
    setIsLoading(true);
    const apiResponse = await createLabelAPI(label);
    if (apiResponse.success) {
      toast(`"${label?.name}" label created`);
      apiResponse.data = {
        ...apiResponse.data,
        id: apiResponse.data.id.toString(),
      };
      addLabelState(convertObjectKeysToCamelCase(apiResponse.data));
    } else {
      toast('Error creating label');
    }
    setIsLoading(false);
  };

  const updateLabel = async (label) => {
    setIsLoading(true);
    const apiResponse = await updateLabelAPI(label);
    if (apiResponse.success) {
      toast(`"${apiResponse?.data?.name}" label updated`);
      updateLabelState(convertObjectKeysToCamelCase(apiResponse.data));
    } else {
      toast('Error updating label');
    }
    setIsLoading(false);
  };

  const deleteLabel = async (label) => {
    const apiResponse = await deleteLabelAPI(label);
    if (apiResponse.success) {
      toast(`"${label?.name}" label deleted`);
      removeLabelState(convertObjectKeysToCamelCase(apiResponse.data));
    } else {
      toast('Error deleting label');
    }
    setIsLoading(false);
  };

  const reorderLabels = async (labelIds, masterSubjectId) => {
    setIsReordering(true);
    const apiResponse = await reorderLabelsAPI(labelIds);
    if (apiResponse.success) {
      setLabels(reorderLabelsUtil(labels, apiResponse.data, masterSubjectId));
    } else {
      toast('Error reordering labels');
    }
    setIsReordering(false);
  };

  const createLabelAssociation = (params) => {
    if (!params.type) throw new Error('Provide the type of assessment.');

    const isVideo = params.type === resourceTypes.video;
    const actionMethod = isVideo
      ? createLabelResourceAssociation
      : createLabelItemAssociation;

    return actionMethod(params);
  };

  const updateLabelAssociations = async (type, resourceId, labelIds) => {
    return await updateLabelAssociationsBulkAPI(type, resourceId, labelIds);
  };

  const createLabelItemAssociations = async ({ quizId, labelIds }) => {
    return await createLabelItemAssociationsBulk({
      quizId,
      labelIds,
    });
  };

  const createLabelResourceAssociations = async ({ resourceId, labelIds }) => {
    return await createLabelResourceAssociationsBulk({
      resourceId,
      labelIds,
    });
  };

  const updateLabelsVisibility = async (isStudentVisible, masterSubjectId) => {
    setIsLoading(true);
    const apiResponse = await updateLabelsVisibilityAPI(
      isStudentVisible,
      masterSubjectId,
    );
    handleTelemetry(
      'teacher_organize',
      TELEMETRY_EVENTS.teacherUpdateShowLabels,
      {
        student_visible: isStudentVisible,
        subjectId: masterSubjectId,
      },
    );
    if (apiResponse.success) {
      const successToastText = isStudentVisible
        ? 'Labels with assignments visible to students'
        : 'Labels hidden from students';
      toast(successToastText);
      updateLabelVisibilityState(isStudentVisible, masterSubjectId);
    } else {
      toast(
        'Error processing backend requests. Please contact the support team.',
      );
    }
    setIsLoading(false);
  };

  // bulk adding mode
  const resourcesForBulkLength = Object.keys(resourcesForBulk).reduce(
    (result, resourceForType) => {
      return result + Object.keys(resourcesForBulk[resourceForType]).length;
    },
    0,
  );

  const activateBulkMode = () => {
    setIsBulkMode(true);
  };

  const deactivateBulkMode = () => {
    setIsBulkMode(false);
    clearBulkSelections();
  };

  const clearBulkSelections = () => {
    setResourcesForBulk(resourcesForBulkInitState);
  };

  const addOrRemoveBulk = (id, type, removeIfExists = true) => {
    /**
     * If we decide to be able to bulk mode PDFs, remove
     * this condition
     */
    if (type === resourceTypes.pdf) {
      return;
    }
    setResourcesForBulk((currentState) => {
      const typeToAdd = [resourceTypes.video, resourceTypes.pdf].includes(type)
        ? type
        : resourceTypes.item;
      const state = window.structuredClone(currentState);

      const currentStateForType = state[typeToAdd];

      if (removeIfExists && currentStateForType[id]) {
        delete currentStateForType[id];
      } else {
        currentStateForType[id] = true;
      }

      return state;
    });
  };

  const checkIfResourceSelectedBulk = (id) =>
    Object.keys(resourcesForBulk).some(
      (resourceForType) => resourcesForBulk[resourceForType][id],
    );

  // might need to memoize
  const value = {
    state: {
      labels,
      isLoading,
      isBulkMode,
      resourcesForBulk,
      resourcesForBulkLength,
      isReordering,
      isTeacherInfoLoading,
    },
    actions: {
      createLabel,
      updateLabel,
      deleteLabel,
      updateLabelsVisibility,
      createLabelAssociation,
      createLabelItemAssociations,
      createLabelResourceAssociations,
      updateLabelAssociations,
      activateBulkMode,
      deactivateBulkMode,
      addOrRemoveBulk,
      checkIfResourceSelectedBulk,
      clearBulkSelections,
      reorderLabels,
    },
  };
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

function useTeacherOrganizeContext({ masterSubject }) {
  const context = React.useContext(Context);
  if (context === undefined) {
    throw new Error(
      'useTeacherOrganizeContext must be used within a TeacherOrganizeProvider',
    );
  }
  const subjectLabels = context.state.labels[masterSubject] || {};

  const { labels = {}, parents = [], children = {} } = subjectLabels;
  const labelIds = Object.keys(labels);

  const isStudentVisible =
    labelIds.length > 0 ? labels[labelIds[0]].studentVisible : false;

  return {
    ...context,
    subjectLabels,
    isStudentVisible,
    labels,
    parents,
    children,
  };
}

export { TeacherOrganizeProvider, useTeacherOrganizeContext };
